import { useCallback, useMemo } from 'react'
import { useDataValue } from 'Simple/Data.js'
import { dateShortWithMonth, dateShortWithMonthAndTime } from 'Data/format.js'
import isFuture from 'date-fns/isFuture'
import { getPayorName, isEntityType, LEDGER_ENTRY_TYPE } from 'Data/payments.js'

import View from './view.js'
import ActionsCell from './ActionsCell/index.js'
import AmountCell from './AmountCell/index.js'
import TypeCell from './TypeCell/index.js'
import PaymentMethodCell from './PaymentMethodCell/index.js'
import NotesCellTooltip from './NotesCellTooltip/index.js'
import Expanded from './Expanded/index.js'

let tooltipValueGetter = params => ({ value: params.value })

export default function Logic(props) {
  let entries = useDataValue({
    context: 'entries',
    viewPath: props.viewPath,
  })
  let filters = useDataValue({
    context: 'tab',
    path: 'selected.ledger.transactions.filter_by',
    viewPath: props.viewPath,
  })
  let showFutureEntries = useDataValue({
    context: 'tab',
    path: 'selected.ledger.show_future_entries',
    viewPath: props.viewPath,
  })

  let columnDefs = useMemo(
    () => [
      {
        field: 'created_at',
        headerName: 'Date / Time',
        initialWidth: 180,
        minWidth: 160,
        valueFormatter: params =>
          params.data.type === LEDGER_ENTRY_TYPE.INSTALLMENT_CHARGE
            ? dateShortWithMonth(params.value)
            : dateShortWithMonthAndTime(params.value),
        cellRenderer: 'agGroupCellRenderer',
      },
      {
        field: 'type',
        headerName: 'Type',
        wrapText: true,
        initialWidth: 140,
        cellRenderer: params => (
          <TypeCell
            {...params}
            key={params.data.id}
            viewPath={`${props.viewPath}/TypeCell(${params.data.id})`}
          />
        ),
      },
      {
        field: 'payment_account',
        headerName: 'Payor',
        initialWidth: 180,
        minWidth: 120,
        valueGetter: params => getPayorName(params.data.payment_account),
        cellStyle: {
          color: '#616E7C',
        },
      },
      {
        field: 'location',
        headerName: 'Location',
        initialWidth: 100,
        minWidth: 100,
        valueGetter: params =>
          params.data.location ? params.data.location.name : '-',
        cellStyle: {
          color: '#616E7C',
        },
      },
      {
        field: 'payment_method',
        headerName: 'Payment method',
        wrapText: true,
        initialWidth: 200,
        cellRenderer: params => (
          <PaymentMethodCell
            {...params}
            key={params.data.id}
            viewPath={`${props.viewPath}/PaymentMethodCell(${params.data.id})`}
          />
        ),
      },
      {
        field: 'notes',
        headerName: 'Notes',
        initialWidth: 300,
        minWidth: 200,
        tooltipValueGetter,
        tooltipComponent: params => (
          <NotesCellTooltip
            {...params}
            viewPath={`${props.viewPath}/NotesCellTooltip(${params.data.id})`}
          />
        ),
        valueGetter: params => getNote(params.data) || '-',
        cellStyle: {
          color: '#52606D',
        },
      },
      {
        field: 'amount',
        headerName: 'Amount',
        initialWidth: 100,
        minWidth: 80,
        cellRenderer: params => (
          <AmountCell
            {...params}
            key={params.data.id}
            viewPath={`${props.viewPath}/AmountCell(${params.data.id})`}
          />
        ),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        initialWidth: 80,
        minWidth: 70,
        cellRenderer: params => (
          <ActionsCell
            {...params}
            key={params.data.id}
            viewPath={`${props.viewPath}/ActionsCell(${params.data.id})`}
          />
        ),
      },
    ],
    [props.viewPath]
  )

  let filteredData = useMemo(() => {
    let data = entries

    if (!showFutureEntries) {
      data = data.filter(item => !isFuture(new Date(item.created_at)))
    }

    // Empty array means "all" - no filtering
    if (filters.length === 0) {
      return data
    }

    return data.filter(item => filters.some(f => isEntityType(item, f)))
  }, [filters, entries, showFutureEntries])

  let getRowStyle = useCallback(params => {
    if (params.data.type === LEDGER_ENTRY_TYPE.PAYMENT) {
      return { backgroundColor: '#F8F9FA' }
    }
  }, [])

  let onRowClicked = useCallback(params => {
    let node = params.node

    let target = params.event.target

    // Don't expand/collapse if clicking on a button or link (e.g. clicking on more actions)
    if (target.closest('button') || target.closest('a')) {
      return
    }

    node.setExpanded(!node.expanded)
  }, [])

  return (
    <View
      {...props}
      columnDefs={columnDefs}
      rowData={filteredData}
      height="100%"
      masterDetail={true}
      detailCellRenderer={params => (
        <Expanded
          {...params}
          key={params.data.id}
          viewPath={`${props.viewPath}/Expanded(${params.data.id})`}
        />
      )}
      detailRowAutoHeight={true}
      onRowClicked={onRowClicked}
      getRowStyle={getRowStyle}
    />
  )
}

function getNote(entry) {
  switch (entry.type) {
    case LEDGER_ENTRY_TYPE.CHARGE:
    case LEDGER_ENTRY_TYPE.ADJUSTMENT:
      return entry.transactions[0].notes
    case LEDGER_ENTRY_TYPE.PAYMENT:
      return entry.payment.notes
    case LEDGER_ENTRY_TYPE.NOTE:
      return entry.note.notes
    default:
      return entry.transaction.notes
  }
}
